import { useEffect, type RefObject } from 'react';

// Alineado con la semántica de isReviewLayerEvent en @bitux/review-layer-react (sin import para no acoplar paquetes).
function isReviewLayerOutsideEvent(event: Event): boolean {
  const path =
    typeof event.composedPath === 'function' ? event.composedPath() : [event.target ?? null];
  for (const n of path) {
    if (!(n instanceof HTMLElement) || !n.closest) continue;
    if (n.closest('[data-review-layer]')) return true;
  }
  return false;
}

interface UseClickOutsideOptions {
  isActive?: boolean;
  disabled?: boolean;
  excludeSelectors?: string[];
  onOutsideClick: () => void;
}

interface UseClickOutsideWithGlobalOptions extends UseClickOutsideOptions {
  elementRef: RefObject<HTMLElement>;
  containerRef?: RefObject<HTMLElement>;
  /** Nodos extra que cuentan como “dentro” (p. ej. trigger externo del Popup). */
  extraInsideRefs?: RefObject<HTMLElement | null>[];
  elementId?: string;
  elementType?: string;
  closeOnClickOutside?: boolean;
  disableSameTypeCheck?: boolean; // Nueva prop para deshabilitar isInsideSameType
}

export const useClickOutside = (
  isActive: boolean,
  elementRef: RefObject<HTMLElement>,
  onOutsideClick: () => void,
  disabled: boolean = false
) => {
  useEffect(() => {
    if (!isActive || disabled) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (isReviewLayerOutsideEvent(event)) return;
      const root = elementRef.current;
      // Avoid false "outside" while the anchored node is not mounted yet (opens then closes on same gesture).
      if (!root) return;
      const target = event.target as Element;
      const isInsideElement = root.contains(target);

      if (!isInsideElement) {
        onOutsideClick();
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isActive, disabled, onOutsideClick, elementRef]);
};

// Hook mejorado para elementos con gestión global
export const useClickOutsideWithGlobal = ({
  isActive = true,
  disabled = false,
  elementRef,
  containerRef,
  extraInsideRefs,
  elementId,
  elementType,
  closeOnClickOutside = true,
  excludeSelectors = [],
  onOutsideClick,
  disableSameTypeCheck = false,
}: UseClickOutsideWithGlobalOptions) => {
  useEffect(() => {
    if (!isActive || disabled || !closeOnClickOutside) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (isReviewLayerOutsideEvent(event)) return;
      const target = event.target as Element;

      // Verificar si el clic está dentro del elemento principal
      const isInsideElement = elementRef.current?.contains(target);

      // Verificar si el clic está dentro del contenedor (si existe)
      const isInsideContainer = containerRef?.current?.contains(target);

      const isInsideExtraRoots =
        extraInsideRefs?.some((r) => r.current && r.current.contains(target)) ?? false;

      // Verificar si el clic está dentro de elementos con el mismo ID
      const isInsideSameElement = elementId
        ? target.closest(`[data-${elementType}-id="${elementId}"]`)
        : false;

      // Verificar si el clic está dentro de elementos del mismo tipo
      const isInsideSameType =
        elementType && !disableSameTypeCheck
          ? target.closest(`[data-${elementType}-container-id="${elementId}"]`)
          : false;

      // Verificar selectores excluidos
      const isInExcludedSelector = excludeSelectors.some((selector) => target.closest(selector));

      // Verificar si el elemento es parte de un componente interactivo
      const isPartOfInteractiveComponent = () => {
        let element = target as Element;
        while (element && element !== document.body) {
          // Buscar atributos data-*-id que indican componentes interactivos
          const attributes = element.attributes;
          for (let i = 0; i < attributes.length; i++) {
            const attr = attributes[i];
            // Excluir atributos de popup y dropdown del elemento actual para evitar conflictos
            if (attr.name.startsWith('data-') && attr.name.endsWith('-id')) {
              // Permitir que dropdowns dentro de popups funcionen normalmente
              if (
                elementType === 'dropdown' &&
                (attr.name.includes('popup') || attr.name.includes('dropdown'))
              ) {
                // Continuar buscando, no considerar este como interactivo
                continue;
              }
              return true;
            }
          }

          // Buscar clases que indican portales o componentes interactivos
          const className = element.className;
          if (typeof className === 'string') {
            const classList = className.split(' ');
            const interactiveClasses = [
              'date-picker-portal',
              'dropdown-portal',
              'modal-portal',
              'tooltip-portal',
              'popover-portal',
              'react-datepicker',
              'react-datepicker-popper',
            ];

            if (interactiveClasses.some((cls) => classList.includes(cls))) {
              return true;
            }

            // Buscar patrones de clases que terminan en -portal
            if (classList.some((cls) => cls.endsWith('-portal'))) {
              return true;
            }
          }

          element = element.parentElement as Element;
        }
        return false;
      };

      const isPartOfInteractive = isPartOfInteractiveComponent();

      // Si está dentro de cualquiera de estos elementos, no cerrar
      if (
        isInsideElement ||
        isInsideContainer ||
        isInsideExtraRoots ||
        isInsideSameElement ||
        isInsideSameType ||
        isInExcludedSelector ||
        isPartOfInteractive
      ) {
        return;
      }

      onOutsideClick();
    };

    const handleCloseEvent = (event: CustomEvent) => {
      if (event.detail.id === elementId) {
        onOutsideClick();
      }
    };

    // Usar capture phase para capturar eventos antes de stopPropagation
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('click', handleClickOutside);

    if (elementId) {
      document.addEventListener(`close-${elementType}`, handleCloseEvent as EventListener);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutside);
      if (elementId) {
        document.removeEventListener(`close-${elementType}`, handleCloseEvent as EventListener);
      }
    };
  }, [
    isActive,
    disabled,
    closeOnClickOutside,
    elementRef,
    containerRef,
    extraInsideRefs,
    elementId,
    elementType,
    excludeSelectors,
    onOutsideClick,
    disableSameTypeCheck,
  ]);
};
